/* eslint no-sequences:0 */
import React from 'react';
import PropTypes from 'prop-types';

import T from './Typography';

const toString = (x) => `${x}`;
const zeroPad = (x, i = 2) => ((x = toString(x)), Array.from({ length: i + 1 - x.length }).join('0') + x);

DateTime.DATE = 'D ^MMM YYYY';

DateTime.propTypes = {
  date: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
  format: PropTypes.string,
};

/**
 * **Access:**
 * ```js
 * import {DateTime} from '@iss/client-react-components'
 * ```
 *
 * Provides a common date renderering component and formatter utility. Leverages native locale data instead of
 * bundling moment.js & its locale library
 */
export default function DateTime({ date, format = DateTime.DATE, ...props }) {
  const v = DateTime.format(date, format);
  return (
    <T {...props} title={v}>
      {v}
    </T>
  );
}

function parseDate(value) {
  if (value == null) {
    return null;
  }

  //Unix Timestamp (seconds) or Java/JS Timestamp (milliseconds)
  if (typeof value === 'number') {
    if (!Number.isInteger(value)) {
      //floats are unix timestamps with millis
      value = Math.floor(value * 1000);
    }
  }

  // new Date(value) never throws... it always results in a Date object...
  // if a hard error occured, it will be "Invalid Date", getTime will return NaN.
  // if a soft error occured, it will be a normal date... so if we really care,
  // we should verify the parsed date some how. ISO date strings are easy...
  // but also very unlikely to fail in any way... :/
  // So... we should just trust the Date() parser.
  const date = new Date(value);

  if (isNaN(date.getTime())) {
    // IE cannot handle this variation of the ISO Date String: '2012-11-02T19:30:00.000+0000', it requires the colon between hours and minutes: "+00:00"
    /* istanbul ignore if */
    if (/\+\d{4}/i.test(value)) {
      return parseDate(value.replace(/(\d\d)$/, ':$1'));
    }
    console.error('The value %o given for date parsing resulted in an invalid date.', value);
    return null;
  }

  return date;
}

// Matching moment.js' formatting tokens using native Date
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString
const FORMATERS = {
  M: (date) => toString(date.getMonth() + 1),
  //Mo: date => 1st, 2nd, 3rd...12th
  MM: (date) => zeroPad(date.getMonth() + 1),
  MMM: (date) => date.toLocaleDateString('en', { month: 'short' }),
  MMMM: (date) => date.toLocaleDateString('en', { month: 'long' }),
  D: (date) => toString(date.getDate()),
  // Do: date => 1st ... 31st
  DD: (date) => zeroPad(date.getDate()),
  // DDD: date => 1 2 3 ... 365
  // DDDo: date => 1st 2nd 3rd ... 365th
  // DDDD: date => zeroPad(FORMATERS.DDD(date), 3),
  d: (date) => toString(date.getDay()), //day of the week
  // do: date => 0th 1st ... 5th 6th
  dd: (date) => date.toLocaleDateString('en', { weekday: 'narrow' }),
  ddd: (date) => date.toLocaleDateString('en', { weekday: 'short' }),
  dddd: (date) => date.toLocaleDateString('en', { weekday: 'long' }),
  // e: date => FORMATERS.d(date),
  // E: date => FORMATERS.e(date) + 1,
  Y: (date) => toString(date.getFullYear()),
  YY: (date) => FORMATERS.Y(date).slice(-2),
  YYYY: (date) => FORMATERS.Y(date),
};

DateTime.format = function (date, format = DateTime.DATE) {
  if (typeof format !== 'string') {
    throw new TypeError('Invalid format string');
  }

  date = parseDate(date);

  format = format.split(/([\^\w]+)/);

  const out = [];

  while (date && format.length > 0) {
    let token = format.shift();
    let cap = /^\^/.test(token);

    token = token.replace(/[\^]+/g, '');

    let f = (FORMATERS[token] || (() => token))(date);

    if (cap) {
      f = f.toUpperCase();
    }

    out.push(f);
  }

  return out.join('');
};
